// Scenario 2: healthy_annual_ng
// Nigerian tenant, BUSINESS x 12 seats, NGN annual. FOUNDER10 applied (10% forever).
// AI credits: 33,600 allocated, 11,000 used, 0 top-up balance. Verve card 1234.

import {
  buildConsumptionEvents,
  buildFeatureBreakdown
} from './ai-credit-usage-fixtures';
import { Scenario, TENANT_ID_NG, daysFromNow } from './scenario.types';
import { SHARED_PLANS, SHARED_TOP_UP_PACKS } from './shared-plans';

const PRICE_PER_SEAT_MINOR = 90000000;   // BUSINESS NGN annual (kobo)
const SEATS = 12;
const SUBTOTAL_MINOR = PRICE_PER_SEAT_MINOR * SEATS;
const DISCOUNT_MINOR = Math.round(SUBTOTAL_MINOR * 0.1);
const TOTAL_MINOR = SUBTOTAL_MINOR - DISCOUNT_MINOR;

export const healthyAnnualNg: Scenario = {
  name: 'healthy_annual_ng',
  subscription: {
    id: 'sub_healthy_annual_ng',
    stripeSubscriptionId: null,
    tenantId: TENANT_ID_NG,
    status: 'active',
    tier: 'BUSINESS',
    billingPeriod: 'annual',
    currency: 'NGN',
    countryCode: 'NG',
    seats: SEATS,
    pricePerSeatMinor: PRICE_PER_SEAT_MINOR,
    subtotalMinor: SUBTOTAL_MINOR,
    discountMinor: DISCOUNT_MINOR,
    taxMinor: 0,
    totalMinor: TOTAL_MINOR,
    appliedCoupon: {
      promotionCode: 'FOUNDER10',
      couponId: 'coupon_founder10',
      name: '10% off forever',
      discountType: 'percent',
      percentOff: 10,
      amountOffMinor: null,
      duration: 'forever',
      durationInMonths: null,
      appliedAt: daysFromNow(-60),
      expiresAt: null
    },
    currentPeriodStart: daysFromNow(-60),
    currentPeriodEnd: daysFromNow(305),
    cancelAtPeriodEnd: false,
    canceledAt: null,
    pastDueSince: null,
    gracePeriodEndsAt: null
  },
  plans: SHARED_PLANS,
  invoices: [
    {
      id: 'inv_ng_001',
      number: 'INV-2026-0021',
      status: 'paid',
      issuedAt: daysFromNow(-60),
      paidAt: daysFromNow(-60),
      periodStart: daysFromNow(-60),
      periodEnd: daysFromNow(305),
      currency: 'NGN',
      subtotalMinor: SUBTOTAL_MINOR,
      discountMinor: DISCOUNT_MINOR,
      taxMinor: 0,
      taxRate: null,
      taxBasis: null,
      totalMinor: TOTAL_MINOR,
      hostedInvoiceUrl: 'https://invoice.stripe.com/mock/inv_ng_001',
      invoicePdfUrl: 'https://invoice.stripe.com/mock/inv_ng_001.pdf',
      lineItems: [
        {
          description: 'Business plan x 12 seats (annual)',
          kind: 'subscription',
          quantity: SEATS,
          unitAmountMinor: PRICE_PER_SEAT_MINOR,
          amountMinor: SUBTOTAL_MINOR,
          periodStart: daysFromNow(-60),
          periodEnd: daysFromNow(305)
        }
      ]
    }
  ],
  receipts: [
    {
      id: 'rcpt_ng_001',
      chargedAt: daysFromNow(-60),
      amountMinor: TOTAL_MINOR,
      currency: 'NGN',
      description: 'Solo subscription renewal (annual)',
      invoiceId: 'inv_ng_001',
      topUpPackId: null
    }
  ],
  paymentMethod: {
    id: 'pm_ng_001',
    type: 'card',
    brand: 'verve',
    last4: '1234',
    expMonth: 11,
    expYear: 2029,
    isDefault: true
  },
  aiCreditBalance: {
    tenantId: TENANT_ID_NG,
    // S8: static ISO dates so the toast dedup key is stable across midnight UTC.
    billingCycleStart: '2026-03-24T00:00:00.000Z',
    billingCycleEnd: '2027-03-24T00:00:00.000Z',
    allocatedCredits: 33600,
    usedCredits: 11000,
    reservedCredits: 0,
    topUpBalance: 0,
    remainingCredits: 33600 - 11000,
    basicTurnsAllocated: 18000,
    basicTurnsUsed: 6200
  },
  topUpPacks: SHARED_TOP_UP_PACKS,
  // ---- #08: AI credit usage ----------------------------------------------
  // 30 events across all 6 feature buckets (heavier business usage).
  // Scale to land on the wallet's 11,000 used.
  consumptionEvents: buildConsumptionEvents({
    cycleAllocated: 33600,
    currentUsed: 11000,
    topUpBalance: 0,
    idPrefix: 'healthy_ng',
    seeds: [
      { featureKey: 'ai_chat', creditsConsumed: 400, modelTier: 'premium', hoursAgo: 720, requestSummary: 'Chat: strategy notes' },
      { featureKey: 'document_extraction', creditsConsumed: 350, modelTier: 'premium', hoursAgo: 700, requestSummary: 'Extract invoice batch' },
      { featureKey: 'voice_transcription', creditsConsumed: 240, modelTier: 'premium', hoursAgo: 680, requestSummary: 'Transcribe sales call' },
      { featureKey: 'image_generation', creditsConsumed: 300, modelTier: 'premium', hoursAgo: 660, requestSummary: 'Generate product image' },
      { featureKey: 'summarization', creditsConsumed: 180, modelTier: 'basic', hoursAgo: 640, requestSummary: 'Summarize weekly report' },
      { featureKey: 'ai_chat', creditsConsumed: 320, modelTier: 'premium', hoursAgo: 620, requestSummary: 'Chat: customer triage' },
      { featureKey: 'document_extraction', creditsConsumed: 380, modelTier: 'premium', hoursAgo: 600, requestSummary: 'Extract supplier invoices' },
      { featureKey: 'voice_transcription', creditsConsumed: 280, modelTier: 'premium', hoursAgo: 580, requestSummary: 'Transcribe board meeting' },
      { featureKey: 'other', creditsConsumed: 120, modelTier: 'basic', hoursAgo: 560, requestSummary: 'Misc utility action' },
      { featureKey: 'ai_chat', creditsConsumed: 250, modelTier: 'premium', hoursAgo: 540, requestSummary: 'Chat: inventory planning' },
      { featureKey: 'image_generation', creditsConsumed: 360, modelTier: 'premium', hoursAgo: 520, requestSummary: 'Generate ad creative' },
      { featureKey: 'document_extraction', creditsConsumed: 400, modelTier: 'premium', hoursAgo: 500, requestSummary: 'Extract receipts' },
      { featureKey: 'summarization', creditsConsumed: 200, modelTier: 'basic', hoursAgo: 480, requestSummary: 'Summarize CRM thread' },
      { featureKey: 'voice_transcription', creditsConsumed: 320, modelTier: 'premium', hoursAgo: 460, requestSummary: 'Transcribe interview' },
      { featureKey: 'ai_chat', creditsConsumed: 340, modelTier: 'premium', hoursAgo: 440, requestSummary: 'Chat: order routing' },
      { featureKey: 'image_generation', creditsConsumed: 280, modelTier: 'premium', hoursAgo: 400, requestSummary: 'Generate banner v2' },
      { featureKey: 'other', creditsConsumed: 90, modelTier: 'basic', hoursAgo: 360, requestSummary: 'Misc utility action' },
      { featureKey: 'document_extraction', creditsConsumed: 360, modelTier: 'premium', hoursAgo: 320, requestSummary: 'Extract contract terms' },
      { featureKey: 'ai_chat', creditsConsumed: 290, modelTier: 'premium', hoursAgo: 290, requestSummary: 'Chat: pricing question' },
      { featureKey: 'summarization', creditsConsumed: 170, modelTier: 'basic', hoursAgo: 250, requestSummary: 'Summarize finance email' },
      { featureKey: 'voice_transcription', creditsConsumed: 260, modelTier: 'premium', hoursAgo: 220, requestSummary: 'Transcribe quick call' },
      { featureKey: 'image_generation', creditsConsumed: 240, modelTier: 'premium', hoursAgo: 180, requestSummary: 'Generate product image v3' },
      { featureKey: 'ai_chat', creditsConsumed: 310, modelTier: 'premium', hoursAgo: 150, requestSummary: 'Chat: rollout plan' },
      { featureKey: 'document_extraction', creditsConsumed: 380, modelTier: 'premium', hoursAgo: 120, requestSummary: 'Extract PO line items' },
      { featureKey: 'voice_transcription', creditsConsumed: 240, modelTier: 'premium', hoursAgo: 96, requestSummary: 'Transcribe daily standup' },
      { featureKey: 'ai_chat', creditsConsumed: 300, modelTier: 'premium', hoursAgo: 72, requestSummary: 'Chat: workflow tweak' },
      { featureKey: 'summarization', creditsConsumed: 160, modelTier: 'basic', hoursAgo: 48, requestSummary: 'Summarize agenda' },
      { featureKey: 'image_generation', creditsConsumed: 320, modelTier: 'premium', hoursAgo: 28, requestSummary: 'Generate launch graphic' },
      { featureKey: 'document_extraction', creditsConsumed: 360, modelTier: 'premium', hoursAgo: 14, requestSummary: 'Extract bank statement' },
      { featureKey: 'ai_chat', creditsConsumed: 280, modelTier: 'premium', hoursAgo: 3, requestSummary: 'Chat: end-of-day wrap' }
    ]
  }),
  featureUsageBreakdown: [],
  topUpHistory: []
};

(healthyAnnualNg.featureUsageBreakdown as unknown) = buildFeatureBreakdown(
  healthyAnnualNg.consumptionEvents ?? []
);
